if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      // GRAMORPH: section cache — begin
      static SECTION_CACHE_MAX_ENTRIES = 24;
      static SECTION_CACHE_MAX_WARM_URLS = 12;
      static SECTION_CACHE_WARM_IDLE_TIMEOUT_MS = 1000;
      // Entries older than this are fetched again, so a price change shows up within this time (risk audit 2026-10-23 A-10).
      static SECTION_CACHE_TTL_MS = 10 * 60 * 1000;

      sectionCache = new Map(); // requestUrl -> { text, storedAt } (insertion order = eviction order)
      sectionCacheInFlight = new Map(); // requestUrl -> Promise<string>
      sectionCacheGeneration = 0;
      sectionCacheUnsubscriber = undefined;
      onSectionCachePageShow = undefined;
      // GRAMORPH: section cache — end

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        // GRAMORPH: section cache — begin
        this.sectionCacheUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.invalidateSectionCache());
        this.onSectionCachePageShow = (event) => {
          if (event.persisted) this.invalidateSectionCache();
        };
        window.addEventListener('pageshow', this.onSectionCachePageShow);
        this.scheduleSectionCacheWarm();
        // GRAMORPH: section cache — end

        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();

        // GRAMORPH: section cache — begin
        this.sectionCacheUnsubscriber?.();
        window.removeEventListener('pageshow', this.onSectionCachePageShow);
        // GRAMORPH: section cache — end
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        // GRAMORPH: 存在しない組み合わせの選び直し — begin
        const updateProductInfo = this.handleUpdateProductInfo(productUrl);
        const handleResponse = (html) => {
          const fallbackOptionValues = this.fallbackOptionValues(html, target);
          if (!fallbackOptionValues) {
            updateProductInfo(html);
            return;
          }
          this.renderProductInfo({
            requestUrl: this.buildRequestUrlWithParams(productUrl, fallbackOptionValues),
            targetId: target.id,
            callback: updateProductInfo,
          });
        };
        // GRAMORPH: 存在しない組み合わせの選び直し — end

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct ? this.handleSwapProduct(productUrl, shouldFetchFullPage) : handleResponse,
        });
      }

      // GRAMORPH: 存在しない組み合わせの選び直し — begin
      // 押したオプションより前を選び直さず、後ろのオプションの選択が、押した値との組み合わせに無いとき
      // （例: ホワイト / Pt900 の状態でイエローを押した）、後ろのオプションを、選べる最初の値（K18 など）に
      // 替えた選択を返す。バリエーションがある、押したのが最後のオプション、選び直せる値が無いときは null。
      fallbackOptionValues(html, target) {
        if (this.getSelectedVariant(html)) return null;
        const groupSelector = 'fieldset';
        const liveGroups = Array.from(this.variantSelectors?.querySelectorAll(groupSelector) ?? []);
        const newGroups = Array.from(this.getVariantSelects(html)?.querySelectorAll(groupSelector) ?? []);
        const targetIndex = liveGroups.findIndex((group) => group.contains(target));
        if (targetIndex < 0 || liveGroups.length !== newGroups.length || targetIndex >= newGroups.length - 1) {
          return null;
        }
        const optionValues = [];
        for (let index = 0; index < newGroups.length; index++) {
          const checked = newGroups[index].querySelector('input[checked]');
          const input =
            index > targetIndex &&
            (!checked || checked.classList.contains('disabled') || checked.classList.contains('visually-disabled'))
              ? newGroups[index].querySelector('input:not(.disabled):not(.visually-disabled)')
              : checked;
          if (!input?.dataset.optionValueId) return null;
          optionValues.push(input.dataset.optionValueId);
        }
        return optionValues;
      }
      // GRAMORPH: 存在しない組み合わせの選び直し — end

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const sourceProductInfo = html.querySelector(selector);
          const variant = this.getSelectedVariant(sourceProductInfo);
          this.variantSelectors?.resolvePendingSelectPromise(variant, this.getVariantSelects(sourceProductInfo));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        const { signal } = this.abortController;

        // GRAMORPH: section cache — begin
        const generation = this.sectionCacheGeneration;
        const fetchSection = () => fetch(requestUrl, { signal }).then((response) => response.text());
        const useCache = !window.Shopify?.designMode;
        const cachedText = useCache ? this.getCachedSection(requestUrl) : undefined;
        const responseTextPromise =
          cachedText !== undefined
            ? Promise.resolve(cachedText)
            : useCache && this.sectionCacheInFlight.has(requestUrl)
              ? this.sectionCacheInFlight.get(requestUrl).catch(() => (signal.aborted ? undefined : fetchSection()))
              : fetchSection();
        // GRAMORPH: section cache — end

        responseTextPromise
          .then((responseText) => {
            if (signal.aborted) return;
            if (
              cachedText === undefined &&
              useCache &&
              requestUrl.includes('section_id=') &&
              generation === this.sectionCacheGeneration &&
              responseText.includes('data-selected-variant')
            ) {
              this.storeSection(requestUrl, responseText);
            }
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
            this.scheduleSectionCacheWarm();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
            this.variantSelectors?.rejectPendingSelectPromise(error);
          });
      }

      parseJsonScript(parent, selector) {
        try {
          return JSON.parse(parent?.querySelector(selector)?.textContent);
        } catch {
          return null;
        }
      }

      getVariantSelects(queryRoot) {
        return queryRoot?.querySelector('variant-selects');
      }

      getSelectedVariant(productInfoNode) {
        return this.parseJsonScript(this.getVariantSelects(productInfoNode), '[data-selected-variant]');
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      // GRAMORPH: section cache — begin
      neighbourSectionUrls() {
        const variantSelects = this.variantSelectors;
        if (!variantSelects) return [];
        const current = variantSelects.selectedOptionValues;
        const groups = Array.from(variantSelects.querySelectorAll('fieldset, .product-form__input--dropdown'));
        if (!current.length || groups.length !== current.length) return [];
        const urls = new Set();
        groups.forEach((group, index) => {
          group.querySelectorAll('[data-option-value-id]').forEach((input) => {
            const productUrl = input.dataset.productUrl;
            if (productUrl && productUrl !== this.dataset.url) return; // combined listing → product swap, never cache
            const candidate = current.slice();
            candidate[index] = input.dataset.optionValueId;
            urls.add(this.buildRequestUrlWithParams(this.dataset.url, candidate));
          });
        });
        return Array.from(urls).slice(0, ProductInfo.SECTION_CACHE_MAX_WARM_URLS);
      }

      scheduleSectionCacheWarm() {
        if (window.Shopify?.designMode) return;
        const run = () => this.isConnected && this.warmSectionCache();
        if (typeof window.requestIdleCallback === 'function') {
          window.requestIdleCallback(run, { timeout: ProductInfo.SECTION_CACHE_WARM_IDLE_TIMEOUT_MS });
        } else {
          setTimeout(run, 200);
        }
      }

      warmSectionCache() {
        this.neighbourSectionUrls().forEach((url) => this.prefetchSection(url));
      }

      prefetchSection(url) {
        const cachedText = this.getCachedSection(url);
        if (cachedText !== undefined) return Promise.resolve(cachedText);
        if (this.sectionCacheInFlight.has(url)) return this.sectionCacheInFlight.get(url);
        const generation = this.sectionCacheGeneration;
        const request = fetch(url, { priority: 'low' })
          .then((response) => {
            if (!response.ok) throw new Error(`section prefetch ${response.status}`);
            return response.text();
          })
          .then((text) => {
            if (!text.includes('data-selected-variant')) throw new Error('section prefetch: unexpected body');
            if (generation === this.sectionCacheGeneration) this.storeSection(url, text);
            return text;
          })
          .finally(() => {
            if (this.sectionCacheInFlight.get(url) === request) this.sectionCacheInFlight.delete(url);
          });
        request.catch(() => {});
        this.sectionCacheInFlight.set(url, request);
        return request;
      }

      getCachedSection(url) {
        const entry = this.sectionCache.get(url);
        if (!entry) return undefined;
        const age = Date.now() - entry.storedAt;
        // A negative age means the device clock went back; treat it as expired too.
        if (age < 0 || age > ProductInfo.SECTION_CACHE_TTL_MS) {
          this.sectionCache.delete(url);
          return undefined;
        }
        return entry.text;
      }

      storeSection(url, text) {
        this.sectionCache.delete(url);
        this.sectionCache.set(url, { text, storedAt: Date.now() });
        while (this.sectionCache.size > ProductInfo.SECTION_CACHE_MAX_ENTRIES) {
          this.sectionCache.delete(this.sectionCache.keys().next().value);
        }
      }

      invalidateSectionCache() {
        this.sectionCacheGeneration += 1;
        this.sectionCache.clear();
        this.sectionCacheInFlight.clear();
        this.scheduleSectionCacheWarm();
      }
      // GRAMORPH: section cache — end

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const sourceVariantSelects = this.getVariantSelects(html);
          const variant = this.getSelectedVariant(html);

          // Resolve product:select promise before updateOptionValues replaces the variant-selects DOM element
          this.variantSelectors?.resolvePendingSelectPromise(variant, sourceVariantSelects);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
